import { Content, ContentRelationshipField } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  TestimonialDocument,
  TestimoniesCardSliceDefault,
  TestimoniesCardSliceWithImage,
} from '../../../prismicio-types';
import { StarIcon } from '@/assets';
import { TitleSection } from '@/components';

enum SLICE_VARIATION {
  default = 'default',
  withImage = 'withImage',
}

const getTypedTestimony = (
  testimony: ContentRelationshipField<'testimonial'>,
) => {
  return testimony as ContentRelationshipField<'testimonial'> &
    TestimonialDocument;
};

/**
 * Props for `TestimoniesCard`.
 */
export type TestimoniesCardProps =
  SliceComponentProps<Content.TestimoniesCardSlice>;

/**
 * Component for "TestimoniesCard" Slices.
 */
const TestimoniesCard = ({ slice }: TestimoniesCardProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="max-w-screen-2xl mx-auto px-5 md:px-16 py-14"
    >
      {slice.variation === SLICE_VARIATION.withImage &&
        renderHeaderSection(slice)}
      <div className="bg-white-gradient rounded-[20px] backdrop-blur-[47.5px] shadow-black-shadow text-black">
        <div
          className={`flex flex-col lg:flex-row justify-between items-center gap-y-4 md:gap-x-20 ${slice.variation === SLICE_VARIATION.default ? 'p-6 md:p-12' : 'px-6 pt-6 lg:p-12'}`}
        >
          {slice.variation === SLICE_VARIATION.default && (
            <TestimonyWithButton slice={slice} />
          )}
          {slice.variation === SLICE_VARIATION.withImage && (
            <TestimonyWithImage slice={slice} />
          )}
        </div>
      </div>
    </section>
  );
};

const renderHeaderSection = (slice: TestimoniesCardSliceWithImage) => {
  return (
    <div className="text-white">
      <TitleSection
        title={slice.primary.title}
        description={slice.primary.subtitle}
      />
    </div>
  );
};

const TestimonyWithButton = ({
  slice,
}: {
  slice: TestimoniesCardSliceDefault;
}) => {
  const {
    primary: { button_label: buttonLabel, button_link: buttonLink, testimony },
  } = slice;

  const typedTestimony = getTypedTestimony(testimony);

  return (
    <>
      {renderTestimony(typedTestimony)}
      <div className="flex">
        <PrismicNextLink field={buttonLink} className="button-filled">
          <span>{buttonLabel}</span>
        </PrismicNextLink>
      </div>
    </>
  );
};

const TestimonyWithImage = ({
  slice,
}: {
  slice: TestimoniesCardSliceWithImage;
}) => {
  const {
    primary: { testimony, image },
  } = slice;

  const typedTestimony = getTypedTestimony(testimony);

  return (
    <>
      {renderTestimony(typedTestimony)}
      <PrismicNextImage field={image} className="max-w-[19rem]" />
    </>
  );
};

const renderTestimony = (testimony: TestimonialDocument) => {
  const starsArray = Array(5).fill(null);

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex gap-x-1">
        {starsArray.map((_, index) => (
          <StarIcon key={`star-${index}`} fill="#03BF9E" />
        ))}
      </div>
      <PrismicRichText
        field={testimony.data.quote}
        components={{
          paragraph: ({ children }) => (
            <p className="text-medium-normal">{children}</p>
          ),
        }}
      />
      <div className="flex items-center gap-x-5">
        <div>
          <PrismicRichText
            field={testimony.data.name}
            components={{
              paragraph: ({ children }) => (
                <p className="font-semibold">{children}</p>
              ),
            }}
          />
          <PrismicRichText field={testimony.data.job_title} />
        </div>
        <figure className="w-[1px] h-[61px] bg-black" />
        <PrismicNextImage field={testimony.data.company_logo} />
      </div>
    </div>
  );
};

export default TestimoniesCard;
